import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  BankingEntitiesPaymentStatusEnum,
  BankingValueObjectsTransferStatusEnum,
  PaymentPaymentRequestsNestedDocument,
  RevokeTransfersToPaymentDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { PaymentBase } from './types'

/**
 * Mapped PaymentRequest that matches the Payment column structure
 * This allows nested table to render PaymentRequests using the same columns
 */
export type MappedPaymentRequestChild = PaymentBase & {
  // Override specific fields to match PaymentRequest data
  date: string | null
  dueDate: string | null
  label: string
  status: BankingEntitiesPaymentStatusEnum
  paymentExecutions: Array<{
    __typename?: 'BankingPaymentExecution'
    id: number
    date: unknown
    executedAt?: unknown
    paidAt?: unknown
    status: BankingEntitiesPaymentStatusEnum
    consent?: { __typename?: 'BankingConsent'; id: number; consentUrl: string } | null
    payer?: unknown
    sepaFile?: unknown
    transfersConnection?: { totalCount: number | null | undefined } | null
  }>
  // Mark as PaymentRequest child and keep original data
  _isPaymentRequest: true
  _paymentRequestData: {
    paymentRequestId: number
    itemsCount: number
    transferIds: number[]
  }
  // Parent payment info
  _parentPaymentId: number
  _parentPaymentStatus: BankingEntitiesPaymentStatusEnum
}

interface ChildrenPaginationInfo {
  total: number
  perPage: number
  currentPage: number
  pagesCount: number
  hasMore: boolean
  cursor?: string | null
}

interface ChildrenResponse {
  records: MappedPaymentRequestChild[]
  type: 'basic' | 'detailed'
  paginationInfo?: ChildrenPaginationInfo
}

/**
 * Calculate aggregated status for a group of transfers
 * Priority: rejected > in_progress > init > paid
 */
const getAggregatedStatus = (
  statuses: BankingValueObjectsTransferStatusEnum[]
): BankingEntitiesPaymentStatusEnum => {
  if (statuses.some((s) => s === 'rejected')) return 'rejected'
  if (statuses.some((s) => s === 'in_progress')) return 'in_progress'
  if (statuses.some((s) => s === 'init')) return 'init'
  if (statuses.every((s) => s === 'paid')) return 'paid'
  return 'init'
}

interface TransferWithPaymentRequest {
  id: number
  amountCents: number
  currency: string
  status: BankingValueObjectsTransferStatusEnum
  paymentRequestItem?: {
    id: number
    paymentRequest?: {
      id: number
      dueDate?: string | null
      label?: string | null
      requester?: {
        id: number
        access: {
          id: number
          avatar?: string | null
          fullName: string
        }
      } | null
    } | null
  } | null
}

interface GroupedPaymentRequest {
  paymentRequestId: number
  label: string
  requesterName: string
  requester: {
    id: number
    fullName: string
    access: {
      id: number
      avatar?: string | null
      fullName: string
    }
  } | null
  dueDate: string | null
  amountCents: number
  currency: string
  itemsCount: number
  statuses: BankingValueObjectsTransferStatusEnum[]
  transferIds: number[]
}

/**
 * Group transfers by PaymentRequest
 */
const groupTransfersByPaymentRequest = (
  transfers: TransferWithPaymentRequest[]
): GroupedPaymentRequest[] => {
  const groups = new Map<number, GroupedPaymentRequest>()

  transfers.forEach((transfer) => {
    const pr = transfer.paymentRequestItem?.paymentRequest
    if (!pr) return

    const existing = groups.get(pr.id)
    if (existing) {
      existing.amountCents += transfer.amountCents
      existing.itemsCount += 1
      existing.statuses.push(transfer.status)
      existing.transferIds.push(transfer.id)
    } else {
      groups.set(pr.id, {
        paymentRequestId: pr.id,
        label: pr.label ?? '-',
        requesterName: pr.requester?.access.fullName ?? '-',
        requester: pr.requester
          ? {
              id: pr.requester.id,
              fullName: pr.requester.access.fullName,
              access: pr.requester.access,
            }
          : null,
        dueDate: pr.dueDate ?? null,
        amountCents: transfer.amountCents,
        currency: transfer.currency,
        itemsCount: 1,
        statuses: [transfer.status],
        transferIds: [transfer.id],
      })
    }
  })

  return Array.from(groups.values())
}

export const usePaymentNestedPaymentRequests = () => {
  const client = useApolloClient()
  const [isRemoving, setIsRemoving] = useState(false)

  const [revokeTransfers] = useMutation(RevokeTransfersToPaymentDocument, {
    extractorFn: (data) => data.bankingMutations.revokeTransfersBankingPayment,
    update: (cache) => {
      // Evict all payment-related queries from cache to force fresh fetch
      cache.evict({ fieldName: 'paymentsConnection' })
      cache.evict({ fieldName: 'payment' })
      cache.evict({ fieldName: 'transfersConnection' })
      cache.gc()
    },
  })

  const itemsWithChildren = (payment: PaymentBase): boolean => {
    return (payment.paymentRequestsCount ?? 0) > 0
  }

  const childrenCount = ({
    item,
  }: {
    item: PaymentBase
    pagination?: ChildrenPaginationInfo
  }): number => {
    return item.paymentRequestsCount ?? 0
  }

  const fetchChildren = async ({
    item,
  }: {
    item: PaymentBase
    filters?: unknown
    pagination?: ChildrenPaginationInfo
  }): Promise<ChildrenResponse> => {
    const executionIds = item.paymentExecutions?.map((execution) => execution.id) ?? []

    if (executionIds.length === 0) {
      return {
        records: [],
        type: 'basic',
      }
    }

    // Fetch all transfers to group by PaymentRequest
    // Paginate through all pages to ensure none are missed
    const transfers: TransferWithPaymentRequest[] = []
    let after: string | undefined
    let hasNextPage = true

    while (hasNextPage) {
      const { data } = await client.query({
        query: PaymentPaymentRequestsNestedDocument,
        variables: {
          paymentExecutionIds: executionIds,
          first: 100,
          after,
        },
        fetchPolicy: 'network-only',
      })

      const nodes =
        data.banking.transfersConnection.edges
          ?.map((edge) => edge?.node)
          .filter((node): node is NonNullable<typeof node> => !!node) ?? []
      transfers.push(...nodes)

      hasNextPage = data.banking.transfersConnection.pageInfo.hasNextPage
      after = data.banking.transfersConnection.pageInfo.endCursor ?? undefined
    }

    const groupedPaymentRequests = groupTransfersByPaymentRequest(transfers)

    // Map to MappedPaymentRequestChild format
    const mappedPaymentRequests: MappedPaymentRequestChild[] = groupedPaymentRequests.map((pr) => ({
      id: pr.paymentRequestId,
      date: null,
      dueDate: pr.dueDate,
      label: pr.label,
      amountCents: pr.amountCents,
      currency: pr.currency,
      status: getAggregatedStatus(pr.statuses),
      paymentExecutions: [],
      requester: pr.requester,
      _isPaymentRequest: true,
      _paymentRequestData: {
        paymentRequestId: pr.paymentRequestId,
        itemsCount: pr.itemsCount,
        transferIds: pr.transferIds,
      },
      _parentPaymentId: item.id,
      _parentPaymentStatus: item.status,
    }))

    return {
      records: mappedPaymentRequests,
      type: 'basic',
      paginationInfo: {
        total: mappedPaymentRequests.length,
        perPage: mappedPaymentRequests.length,
        currentPage: 1,
        pagesCount: 1,
        hasMore: false,
      },
    }
  }

  const removePaymentRequestFromPayment = async (
    paymentId: number,
    transferIds: number[],
    onSuccess?: () => void
  ) => {
    setIsRemoving(true)

    try {
      const result = await revokeTransfers({
        variables: {
          paymentId,
          transferIds,
        },
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('banking.bulkPayments.detail.removeError'),
        })
      })

      if (handled.success) {
        // Force refetch all active queries to get fresh data
        await client.refetchQueries({
          include: 'active',
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('banking.bulkPayments.detail.removeSuccess'),
        })
        onSuccess?.()
      }

      return handled
    } finally {
      setIsRemoving(false)
    }
  }

  return {
    itemsWithChildren,
    childrenCount,
    fetchChildren,
    removePaymentRequestFromPayment,
    isRemoving,
  }
}
